import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import TitledCard from "@/components/common/TitledCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import PageHeaderActions from "@/components/common/PageHeaderActions";
import { useBankAccounts } from "@/hooks/useBankAccounts";
import { useCompany } from "@/hooks/useCompany";
import { SalesReport } from "@/types/salesReport";
import SekEquivalent from "@/components/shared/SekEquivalent";
import MarkAsReceivedModal from "@/components/salesReports/MarkAsReceivedModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, CheckCircle, Circle, HandCoins } from "lucide-react";
import { formatAmount } from "@financialharmony/ui";

const SalesReports = () => {
  const t = useTranslations("consultantBooks.salesReports");
  // BUG (fixed here, pre-existing): this previously destructured `activeCompany` from
  // useAuth(), a field AuthContextType never had — always undefined, so fetchSalesReports's
  // guard below silently no-opped and the page never loaded real data in production. Every
  // other page (e.g. Messages.tsx) sources the active company from useCompany() instead.
  const { company } = useCompany();
  const [activeTab, setActiveTab] = useState<string>("all");
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [salesReports, setSalesReports] = useState<SalesReport[]>([]);

  const { accounts } = useBankAccounts();
  const hasBankAccounts = accounts.length > 0;

  const [markAsReceivedOpen, setMarkAsReceivedOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<SalesReport | null>(null);

  const fetchSalesReports = async () => {
    if (!company?.id) return;
    try {
      const service = ServiceRegistry.getSalesReportService();
      const data = await service.getSalesReports(company.id);
      setSalesReports(data);
    } catch (error) {
      console.error("Failed to fetch sales reports:", error);
      toast.error(t("fetchFailedTitle"), { description: t("fetchFailed") });
    }
  };

  useEffect(() => {
    fetchSalesReports();
  }, [company?.id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSalesReports();
    setRefreshing(false);
  };

  const filteredReports = useMemo(() => {
    if (activeTab === "all") return salesReports;
    if (activeTab === "unpaid") return salesReports.filter(r => r.status === "unpaid");
    if (activeTab === "paid") return salesReports.filter(r => r.status === "paid");
    if (activeTab === "manually_handled") return salesReports.filter(r => r.status === "manually_handled");
    return salesReports;
  }, [salesReports, activeTab]);

  const handleMarkAsReceived = (report: SalesReport) => {
    setSelectedReport(report);
    setMarkAsReceivedOpen(true);
  };

  const handleMarkAsReceivedComplete = async () => {
    setMarkAsReceivedOpen(false);
    setSelectedReport(null);
    await fetchSalesReports();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />{t("statuses.paid")}</Badge>;
      case "manually_handled":
        return <Badge variant="secondary"><HandCoins className="w-3 h-3 mr-1" />{t("statuses.manually_handled")}</Badge>;
      default:
        return <Badge variant="outline"><Circle className="w-3 h-3 mr-1" />{t("statuses.unpaid")}</Badge>;
    }
  };

  const formatPaymentMethods = (methods: SalesReport["paymentMethods"]) => {
    if (!methods || methods.length === 0) return "—";
    return methods.map(m => {
      const label = m.provider ? `${m.method} (${m.provider})` : m.method;
      return `${label}: ${formatAmount(m.amount)}`;
    }).join(", ");
  };

  const formatCurrency = (amount: number | null, currency: string) => {
    if (amount === null) return "—";
    return `${formatAmount(amount)} ${currency}`;
  };

  return (
    <div className="space-y-6">
      <PageHeaderActions
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <TitledCard title={t("title")} description={t("subtitle")}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">{t("tabs.all", { count: String(salesReports.length) })}</TabsTrigger>
              <TabsTrigger value="unpaid">
                {t("tabs.unpaid", { count: String(salesReports.filter(r => r.status === "unpaid").length) })}
              </TabsTrigger>
              <TabsTrigger value="paid">
                {t("tabs.paid", { count: String(salesReports.filter(r => r.status === "paid").length) })}
              </TabsTrigger>
              <TabsTrigger value="manually_handled">
                {t("tabs.manuallyHandled", { count: String(salesReports.filter(r => r.status === "manually_handled").length) })}
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-4">
              {filteredReports.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {t("empty")}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("columns.reportDate")}</TableHead>
                      <TableHead>{t("columns.totalSales")}</TableHead>
                      <TableHead>{t("columns.vat")}</TableHead>
                      <TableHead>{t("columns.paymentMethods")}</TableHead>
                      <TableHead>{t("columns.status")}</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReports.map((report) => {
                      const totalVat = report.vatSummary
                        ? report.vatSummary.reduce((sum, v) => sum + v.vat_amount, 0)
                        : null;

                      return (
                        <TableRow key={report.id}>
                          <TableCell className="font-medium">{report.reportDate}</TableCell>
                          <TableCell>
                            {formatCurrency(report.totalNetSales, report.currency)}
                            <SekEquivalent sekAmount={report.sekAmount} currency={report.currency} />
                          </TableCell>
                          <TableCell>{formatCurrency(totalVat, report.currency)}</TableCell>
                          <TableCell className="text-sm">{formatPaymentMethods(report.paymentMethods)}</TableCell>
                          <TableCell>{getStatusBadge(report.status)}</TableCell>
                          <TableCell>
                            {report.status === "unpaid" && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleMarkAsReceived(report)}>
                                    {t("markAsReceived")}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
      </TitledCard>

      {selectedReport && (
        <MarkAsReceivedModal
          open={markAsReceivedOpen}
          onOpenChange={setMarkAsReceivedOpen}
          salesReport={selectedReport}
          hasBankAccounts={hasBankAccounts}
          onComplete={handleMarkAsReceivedComplete}
        />
      )}
    </div>
  );
};

export default SalesReports;
